// src/utils/layout/setupFloorPlanInLayout.js
//
// Kat plani, vaziyet plani ve ground floor clone ve layout icin hazirlama.

import * as THREE from 'three';
import { Text as TroikaText } from 'troika-three-text';
import { hideOutlierMeshes, computeVisibleBoundingBox } from './layoutHelpers';
import { createFloorPlanMesh } from '../../../modules/fpSync';
import { flog } from '../../../utils/forensics';
import { DOCUMENT_OFFSET } from './layoutConstants';

function restoreOriginalColors(group) {
  group.traverse((child) => {
    if (child.userData?.originalColor && child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((mat, index) => {
          if (mat.color && child.userData.originalColor[index] !== undefined) {
            mat.color.set(child.userData.originalColor[index]);
          }
        });
      } else if (child.material.color) {
        child.material.color.set(child.userData.originalColor);
      }
    }
  });
}

// DXF mtext (troika) container'ı group.position'ı entity insertion noktasında tutar,
// iç troika mesh'leri relative offset taşır. resetChildPositions bunları (0,0,0) yaparsa
// tüm mtext'ler origin'e yığılır (troika geometry baked olamaz, line'lar gibi). userData.type
// floor plan import'unda kaybolabildiği için KİMLİK-tabanlı koru: troika Text node'ları
// (sync+text) VE onları taşıyan container parent'ları (mtext group) pozisyonu korunur.
// (Single-line text canvas-sprite, sync/text yok → yolu değişmez.)
function resetChildPositions(group) {
  const protect = new Set();
  group.traverse((o) => {
    if (typeof o?.sync === 'function' && typeof o?.text === 'string') {
      protect.add(o);
      if (o.parent && o.parent !== group) protect.add(o.parent); // mtext container (insertion noktası)
    }
  });
  group.traverse((child) => {
    if (child === group || protect.has(child)) return;
    if (child.position) child.position.set(0, 0, 0);
  });
}

// troika-three-text (DXF mtext) Text mesh'leri clone(true) ile DÜZGÜN kopyalanmaz:
// troika copy() metin özelliklerini kopyalar ama (a) geometry'yi taze/boş bırakır ve
// (b) material REFERANSINI PAYLAŞTIRIR. troika derived material instance başına olmalı;
// paylaşımlı material → "Vertex shader is not compiled / MeshBasicMaterial" shader
// hatası → mtext görünmez. (Single-line text canvas-sprite olduğu için clone'da görünür.)
// Bu yüzden mimari paftada görünen mtext, clone edilen Kat Planı (Kuvvetli Akım vb.)
// paftada yoktu. ÇÖZÜM: clone'daki bozuk troika Text'leri SIFIRDAN yeni troika Text ile
// değiştir (kendi material + geometry; material'ı KOPYALAMA → troika fresh türetir), sync et.
const TROIKA_CLONE_PROPS = [
  'font', 'fontSize', 'fontStyle', 'fontWeight', 'lang', 'letterSpacing', 'lineHeight',
  'maxWidth', 'overflowWrap', 'text', 'direction', 'textAlign', 'textIndent', 'whiteSpace',
  'anchorX', 'anchorY', 'colorRanges', 'sdfGlyphSize',
  'color', 'depthOffset', 'clipRect', 'curveRadius', 'orientation', 'glyphGeometryDetail',
];

// textScale: kat planı mtext'lerini büyütmek için (topraklama + kat planları 1.5×).
// Vaziyet planı varsayılan 1 ile çağırır (ölçeklenmez).
function recreateTroikaText(group, textScale = 1) {
  const broken = [];
  group.traverse((o) => {
    // troika Text: sync() metodu + string text özelliği olan node. Single-line
    // canvas-sprite text'lerde sync/text yok → onlara dokunulmaz.
    if (typeof o?.sync === 'function' && typeof o?.text === 'string') broken.push(o);
  });
  if (!broken.length) return;
  // FLATTEN SHIFT (V): Floor plan geometry'si (line + single-line text) baked/merkezlenmiş
  // koordinata kaydırılmış AMA troika mtext'in userData.position'ı ORİJİNAL DXF uzayında
  // kalmış (troika geometry baked olamadığı için). Çalışan single-line text'lerden
  // (baked geometry merkezi − kendi userData.position'ı) V'yi ölç (ortalama, anchor
  // offsetini bastırmak için), mtext udp'lerine uygula → çizgilerle hizalanır.
  group.updateMatrixWorld(true);
  const V = new THREE.Vector3();
  let vCount = 0;
  group.traverse((o) => {
    const udp = o.userData?.position;
    const isTroika = typeof o?.sync === 'function' && typeof o?.text === 'string';
    if (o.userData?.type === 'text' && !isTroika && udp && typeof udp.x === 'number') {
      const b = new THREE.Box3().setFromObject(o);
      if (!b.isEmpty()) {
        const c = b.getCenter(new THREE.Vector3());
        V.x += c.x - udp.x; V.y += c.y - udp.y; vCount++;
      }
    }
  });
  if (vCount > 0) { V.divideScalar(vCount); V.z = 0; }
  for (const old of broken) {
    const parent = old.parent;
    if (!parent) continue;
    const fresh = new TroikaText();
    fresh.gpuAccelerateSDF = false; // GPU'da ANGLE_instanced_arrays yoksa hata → JS path'e zorla
    TROIKA_CLONE_PROPS.forEach((p) => { if (old[p] !== undefined) fresh[p] = old[p]; });
    // RENK: çok-segmentli mtext inner troika'da .color prop tanımsız/default (beyaz)
    // kalabiliyor → fresh beyaz oluşup ancak hover'da (child.sync) düzeliyordu. Rengi
    // userData'dan (segmentColor → originalColor → renderedColor) garanti et.
    const col = old.userData?.segmentColor ?? old.userData?.originalColor
      ?? old.userData?.renderedColor ?? old.color;
    if (col !== undefined && col !== null) fresh.color = col;
    // Floor plan mtext'lerinde mesh.position (0,0,0)'a sıfırlanmış; gerçek DXF insertion
    // noktası userData.position'da saklı. Konumu userData.position + V (flatten shift) ile
    // çizgi uzayına taşı.
    // - Single-mesh mtext: insertion troika'nın KENDİ userData.position'ında.
    // - Çok-segmentli mtext: insertion CONTAINER'da (parent.userData.position); container'ı
    //   konumla, iç segment troika relative offset'ini korusun.
    // ÖNCELİK: bakeWorldTransform (site/floor plan yakalama) troika'nın world anchor
    // pozisyonunu + scale'ini userData.bakedTextPos/bakedTextScale'e yazdıysa onu DOĞRUDAN
    // kullan (V uygulama). Bu, scale'li seçimleri (vaziyet planı) doğru hizalar — çizgiler
    // de aynı world uzayına baked. fontSize'ı bakedTextScale ile ölçekle.
    const btp = old.userData?.bakedTextPos;
    const udp = old.userData?.position;
    const parentUdp = parent.userData?.position;
    if (btp && typeof btp.x === 'number') {
      fresh.position.set(btp.x, btp.y, btp.z || 0);
      const bs = old.userData?.bakedTextScale;
      if (bs && bs !== 1 && typeof fresh.fontSize === 'number') fresh.fontSize *= bs;
    } else if (udp && typeof udp.x === 'number') {
      fresh.position.set(udp.x + V.x, udp.y + V.y, udp.z || 0);
    } else if (parent !== group && parentUdp && typeof parentUdp.x === 'number') {
      parent.position.set(parentUdp.x + V.x, parentUdp.y + V.y, parentUdp.z || 0);
      fresh.position.copy(old.position);
    } else {
      fresh.position.copy(old.position);
    }
    // Rotasyon: quaternion sıfırlanmışsa userData.rotation'dan (derece) uygula.
    const isIdentityQuat = old.quaternion.x === 0 && old.quaternion.y === 0 && old.quaternion.z === 0 && old.quaternion.w === 1;
    if (isIdentityQuat && old.userData?.rotation) {
      fresh.rotation.z = (old.userData.rotation * Math.PI) / 180;
    } else {
      fresh.quaternion.copy(old.quaternion);
    }
    fresh.scale.copy(old.scale);
    fresh.name = old.name;
    fresh.userData = { ...old.userData };
    fresh.renderOrder = old.renderOrder;
    fresh.frustumCulled = old.frustumCulled;
    fresh.visible = old.visible;
    // Kat planı text büyütme (1.5×). fontSize'ın son halini (bakedTextScale dahil) ölçekle.
    if (textScale !== 1 && typeof fresh.fontSize === 'number') fresh.fontSize *= textScale;
    parent.add(fresh);
    parent.remove(old);
    try { old.dispose?.(); } catch (_) { /* dispose hatası kritik değil */ }
    fresh.sync();
  }
}

export function setupVaziyetPlani(docDef, documentGroup) {
  const clone = docDef.group.clone(true);
  recreateTroikaText(clone); // clone'da boş kalan mtext geometry'lerini regenere et
  clone.userData.isLiveFloorPlanGroup = true;
  clone.userData.floorPlanId = docDef.floorPlanId;
  restoreOriginalColors(clone);
  clone.position.set(0, 0, 0);
  const bbox = new THREE.Box3().setFromObject(clone);
  const center = bbox.getCenter(new THREE.Vector3());
  clone.position.sub(center);
  clone.position.add(new THREE.Vector3(docDef.w / 2, -docDef.h / 2 - DOCUMENT_OFFSET, 0));
  documentGroup.add(clone);
}

export function setupKatPlani(docDef, documentGroup, projectMeshSize, dispatch, updateFloorPlan, FLOOR_PLAN_TAB, savedFpGroupPos) {
  const floorPlanGroup = docDef.group.clone(true);
  recreateTroikaText(floorPlanGroup, 1.5); // clone'da boş mtext'leri regenere et + textleri 1.5× büyüt
  floorPlanGroup.userData.isLiveFloorPlanGroup = true;
  floorPlanGroup.userData.floorPlanId = docDef.floorPlanId;

  const _planSize = Math.max(docDef.w || 2000, docDef.h || 1500);
  hideOutlierMeshes(floorPlanGroup, _planSize, `Kat Plan\u0131/${(docDef.floorPlanId || '').slice(-8)}`);
  restoreOriginalColors(floorPlanGroup);
  resetChildPositions(floorPlanGroup);

  // Saved fpGroup position varsa bbox centering'i atla — DXF reimport
  // non-determinizminden kaynaklanan kaymayi engeller.
  const useSavedPos = savedFpGroupPos && projectMeshSize.width > 0 && projectMeshSize.height > 0;
  if (!useSavedPos) {
    floorPlanGroup.position.set(0, 0, 0);
    floorPlanGroup.updateMatrixWorld(true);
    const bbox = computeVisibleBoundingBox(floorPlanGroup);
    const center = bbox.getCenter(new THREE.Vector3());
    floorPlanGroup.position.sub(center);
  }
  const layoutOffset = new THREE.Vector3(docDef.w / 2, -docDef.h / 2 - DOCUMENT_OFFSET, 0);

  if (projectMeshSize.width > 0 && projectMeshSize.height > 0 && docDef.floorPlanId) {
    const fpMesh = createFloorPlanMesh({
      width: projectMeshSize.width, height: projectMeshSize.height,
      floorPlanId: docDef.floorPlanId,
    });
    fpMesh.position.set(
      layoutOffset.x - projectMeshSize.width / 2,
      layoutOffset.y - projectMeshSize.height / 2, 0
    );
    fpMesh.userData.skipSerialize = true;
    documentGroup.add(fpMesh);
    if (useSavedPos) {
      floorPlanGroup.position.set(savedFpGroupPos.x, savedFpGroupPos.y, savedFpGroupPos.z || 0);
    } else {
      floorPlanGroup.position.add(new THREE.Vector3(projectMeshSize.width / 2, projectMeshSize.height / 2, 0));
    }
    fpMesh.add(floorPlanGroup);
    flog('FAZ11_FPMESH_OLUSTURULDU', {
      floorPlanId: docDef.floorPlanId,
      fpMeshPos: fpMesh.position.toArray(),
      fpMeshWorldPos: fpMesh.getWorldPosition(new THREE.Vector3()).toArray(),
      fpGroupLocalPos: floorPlanGroup.position.toArray(),
      meshSize: { w: projectMeshSize.width, h: projectMeshSize.height },
      restoredFromSave: useSavedPos,
    });
  } else {
    floorPlanGroup.position.add(layoutOffset);
    documentGroup.add(floorPlanGroup);
  }

  // Redux'a objects yaz (wall detection icin)
  const lineObjects = [];
  floorPlanGroup.traverse(child => {
    if (child.type === "Line") {
      child.frustumCulled = false;
      child.updateMatrixWorld(true);
      lineObjects.push(child);
    }
  });
  dispatch(updateFloorPlan({ id: docDef.floorPlanId, changes: { objects: lineObjects }, tabId: FLOOR_PLAN_TAB }));
}

export function setupGroundFloor(docDef, documentGroup) {
  const floorPlanGroup = docDef.group.clone(true);
  recreateTroikaText(floorPlanGroup, 1.5); // clone'da boş mtext'leri regenere et + textleri 1.5× büyüt
  floorPlanGroup.userData.isLiveFloorPlanGroup = true;
  floorPlanGroup.userData.floorPlanId = docDef.floorPlanId;

  const _gfPlanSize = Math.max(docDef.w || 2000, docDef.h || 1500);
  hideOutlierMeshes(floorPlanGroup, _gfPlanSize, `Ground Floor/${(docDef.floorPlanId || '').slice(-8)}`);
  restoreOriginalColors(floorPlanGroup);
  resetChildPositions(floorPlanGroup);

  floorPlanGroup.position.set(0, 0, 0);
  floorPlanGroup.updateMatrixWorld(true);
  const bbox = computeVisibleBoundingBox(floorPlanGroup);
  const center = bbox.getCenter(new THREE.Vector3());
  floorPlanGroup.position.sub(center);
  floorPlanGroup.position.add(new THREE.Vector3(docDef.w / 2, -docDef.h / 2 - DOCUMENT_OFFSET, 0));
  documentGroup.add(floorPlanGroup);
}
